
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Activity, Search, MessageCircle, Bookmark, User, Settings, LogOut, Sparkles, Building2, LogIn, ChevronDown, Calculator, Percent, Landmark, FileText, Briefcase, Lock, BarChart3 } from 'lucide-react';
import { openIntercom, isIntercomConfigured } from '../lib/intercom';
import UpgradeModal from './UpgradeModal';
import { SearchAutocomplete } from './SearchAutocomplete';
import { Property, UserProfile } from '../types';
import { getProjectUrl } from '../lib/slug';
import { authGate } from '../lib/authGate';

interface NavbarProps {
  shortlistCount: number;
  onNavigateToShortlist: () => void;
  onNavigateHome: () => void;
  onNavigateToFinder: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCalculator: () => void;
  onNavigateToRentalYield: () => void;
  onNavigateToMortgage: () => void;
  onNavigateToAnalytics: () => void;
  onNavigateToDeveloperHub: () => void;
  onNavigateToPortfolio: () => void;
  onLogout: () => void;
  onSearch?: (query: string) => void;
  isGuest?: boolean;
  userProfile?: UserProfile | null;
  onPropertyClick?: (id: string | number, property?: Property) => void;
}

const Navbar: React.FC<NavbarProps> = ({
  shortlistCount,
  onNavigateToShortlist,
  onNavigateHome,
  onNavigateToFinder,
  onNavigateToProfile,
  onNavigateToSettings,
  onNavigateToCalculator,
  onNavigateToRentalYield,
  onNavigateToMortgage,
  onNavigateToAnalytics,
  onNavigateToDeveloperHub,
  onNavigateToPortfolio,
  onLogout,
  onSearch,
  isGuest,
  userProfile,
  onPropertyClick
}) => {
  // Check if user has investor account
  const hasInvestorAccount = userProfile?.buyerType === 'Investor';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if menu is open AND click is outside the menu container
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Also check if we didn't click the trigger button (optional, but usually overlay handles this)
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <nav className="glass-nav w-full h-16 flex items-center justify-between px-4 lg:px-12 transition-all duration-300">

        {/* Left: Hamburger + Logo */}
        <div className="flex items-center gap-4 lg:gap-12">
          {/* Mobile Menu Trigger */}
          <button
            onClick={() => setIsMenuOpen(true)}
            className="md:hidden p-2 text-slate-500 hover:text-slate-900"
          >
            <div className="space-y-1.5">
              <span className="block w-5 h-0.5 bg-current rounded-full"></span>
              <span className="block w-5 h-0.5 bg-current rounded-full"></span>
              <span className="block w-5 h-0.5 bg-current rounded-full"></span>
            </div>
          </button>

          <button onClick={onNavigateHome} className="flex items-center group">
            <span className="text-base font-bold tracking-tight text-slate-900 font-display">
              Off Plan Marketplace
            </span>
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={onNavigateHome} 
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/marketplace' || location.pathname === '/' 
                  ? 'text-slate-900' 
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Marketplace
            </button>
            <button 
              onClick={onNavigateToPortfolio} 
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/portfolio' 
                  ? 'text-slate-900' 
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Portfolio
            </button>

            {/* Tools Dropdown */}
            <div className="relative group h-16 flex items-center">
              <button
                className={`text-sm font-medium transition-colors flex items-center gap-1.5 h-full ${
                  location.pathname.startsWith('/tools') || location.pathname === '/analytics'
                    ? 'text-slate-900' 
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Tools
                <ChevronDown size={14} className="text-slate-400 group-hover:rotate-180 transition-transform duration-300" />
              </button>

              <div className="absolute top-full left-0 w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-2 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-50">
                <div className="px-4 py-2 border-b border-slate-50">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Analysis Suite</span>
                </div>

                {/* ROI Calculator - Requires Investor Account */}
                <button
                  onClick={() => {
                    if (isGuest) {
                      authGate.openGate('calculator', {
                        path: location.pathname,
                        action: 'access-calculator',
                        targetPath: '/tools/calculator'
                      });
                    } else if (!hasInvestorAccount) {
                      window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                        detail: { path: '/tools/calculator' }
                      }));
                    } else {
                      onNavigateToCalculator();
                    }
                  }}
                  className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-3 transition-colors ${
                    !isGuest && !hasInvestorAccount
                      ? 'text-slate-400 opacity-60 hover:bg-slate-50'
                      : location.pathname === '/tools/calculator'
                      ? 'bg-slate-50 text-slate-900 hover:bg-slate-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    !isGuest && !hasInvestorAccount
                      ? 'bg-slate-100 text-slate-400'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    <Calculator size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">ROI Calculator</p>
                    <p className="text-xs text-slate-500">Capital appreciation & exit strategy</p>
                  </div>
                  {!isGuest && !hasInvestorAccount && (
                    <Lock size={14} className="text-amber-500 shrink-0" />
                  )}
                </button>

                {/* Rental Yield - Open to all logged-in users */}
                <button
                  onClick={onNavigateToRentalYield}
                  className={`w-full text-left px-4 py-3 text-sm font-medium hover:bg-slate-50 flex items-center gap-3 transition-colors ${
                    location.pathname === '/tools/rental-yield' ? 'bg-slate-50 text-slate-900' : 'text-slate-700'
                  }`}
                >
                  <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                    <Percent size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">Rental Yield</p>
                    <p className="text-xs text-slate-500">Gross & net yield calculations</p>
                  </div>
                </button>

                {/* Mortgage Estimator - Requires Investor Account */}
                <button
                  onClick={() => {
                    if (isGuest) {
                      authGate.openGate('mortgage', {
                        path: location.pathname,
                        action: 'access-mortgage',
                        targetPath: '/tools/mortgage'
                      });
                    } else if (!hasInvestorAccount) {
                      window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                        detail: { path: '/tools/mortgage' }
                      }));
                    } else {
                      onNavigateToMortgage();
                    }
                  }}
                  className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-3 transition-colors ${
                    !isGuest && !hasInvestorAccount
                      ? 'text-slate-400 opacity-60 hover:bg-slate-50'
                      : location.pathname === '/tools/mortgage'
                      ? 'bg-slate-50 text-slate-900 hover:bg-slate-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    !isGuest && !hasInvestorAccount
                      ? 'bg-slate-100 text-slate-400'
                      : 'bg-blue-50 text-blue-600'
                  }`}>
                    <Landmark size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">Mortgage Estimator</p>
                    <p className="text-xs text-slate-500">UAE mortgage calculations</p>
                  </div>
                  {!isGuest && !hasInvestorAccount && (
                    <Lock size={14} className="text-amber-500 shrink-0" />
                  )}
                </button>

                <div className="my-1 border-t border-slate-50"></div>

                {/* Market Analysis - Requires Investor Account */}
                <button
                  onClick={() => {
                    if (isGuest) {
                      authGate.openGate('analytics', {
                        path: location.pathname,
                        action: 'access-analytics',
                        targetPath: '/analytics'
                      });
                    } else if (!hasInvestorAccount) {
                      window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                        detail: { path: '/analytics' }
                      }));
                    } else {
                      onNavigateToAnalytics();
                    }
                  }}
                  className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-3 transition-colors ${
                    !isGuest && !hasInvestorAccount
                      ? 'text-slate-400 opacity-60 hover:bg-slate-50'
                      : location.pathname === '/analytics'
                      ? 'bg-slate-50 text-slate-900 hover:bg-slate-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    !isGuest && !hasInvestorAccount
                      ? 'bg-slate-100 text-slate-400'
                      : 'bg-indigo-50 text-indigo-600'
                  }`}>
                    <BarChart3 size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">Market Analysis</p>
                    <p className="text-xs text-slate-500">Market trends & insights</p>
                  </div>
                  {!isGuest && !hasInvestorAccount && (
                    <Lock size={14} className="text-amber-500 shrink-0" />
                  )}
                </button>

                {/* Developer Intel - Requires Investor Account */}
                <button
                  onClick={() => {
                    if (isGuest) {
                      authGate.openGate('developer-hub', {
                        path: location.pathname,
                        action: 'access-developer-hub',
                        targetPath: '/developer-hub'
                      });
                    } else if (!hasInvestorAccount) {
                      window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                        detail: { path: '/developer-hub' }
                      }));
                    } else {
                      onNavigateToDeveloperHub();
                    }
                  }}
                  className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-3 transition-colors ${
                    !isGuest && !hasInvestorAccount
                      ? 'text-slate-400 opacity-60 hover:bg-slate-50'
                      : location.pathname === '/developer-hub'
                      ? 'bg-slate-50 text-slate-900 hover:bg-slate-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    !isGuest && !hasInvestorAccount
                      ? 'bg-slate-100 text-slate-400'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    <Briefcase size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">Developer Intel</p>
                    <p className="text-xs text-slate-500">Due diligence & track records</p>
                  </div>
                  {!isGuest && !hasInvestorAccount && (
                    <Lock size={14} className="text-amber-500 shrink-0" />
                  )}
                </button>
              </div>
            </div>


          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 md:gap-4">
          {/* ... existing right actions ... */}

          {/* Mobile Menu Overlay - Portalled to Body */}
          {isMenuOpen && createPortal(
            <div className="fixed inset-0 z-[100] md:hidden">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
              <div ref={menuRef} className="absolute top-0 bottom-0 left-0 w-3/4 max-w-sm bg-white shadow-2xl p-6 flex flex-col gap-6 animate-[slideInLeft_0.3s_ease-out]">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold font-display text-slate-900">Menu</h2>
                  <button onClick={() => setIsMenuOpen(false)} className="p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"><LogOut className="rotate-180" size={18} /></button>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-2 pb-4 border-b border-slate-100">
                  <button onClick={() => { onNavigateHome(); setIsMenuOpen(false); }} className="p-3 bg-slate-50 rounded-lg text-left hover:bg-slate-100 transition-colors min-h-[60px] flex flex-col justify-center">
                    <Building2 size={20} className="text-slate-600 mb-1" />
                    <p className="text-xs font-semibold text-slate-900">Marketplace</p>
                  </button>
                  <button onClick={() => { onNavigateToShortlist(); setIsMenuOpen(false); }} className="p-3 bg-slate-50 rounded-lg text-left hover:bg-slate-100 transition-colors min-h-[60px] flex flex-col justify-center relative">
                    <Bookmark size={20} className="text-slate-600 mb-1" />
                    <p className="text-xs font-semibold text-slate-900">Shortlist</p>
                    {shortlistCount > 0 && (
                      <span className="absolute top-2 right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                        {shortlistCount > 99 ? '99+' : shortlistCount}
                      </span>
                    )}
                  </button>
                </div>

                <div className="space-y-1 flex-1 overflow-y-auto">
                  <button onClick={() => { onNavigateToPortfolio(); setIsMenuOpen(false); }} className="w-full text-left py-3 px-2 font-medium text-slate-700 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <Bookmark size={18} /> Portfolio
                  </button>

                  {/* Property Finder disabled until later */}
                  {/* <button onClick={() => { onNavigateToFinder(); setIsMenuOpen(false); }} className="w-full text-left py-2 font-medium text-slate-700 flex items-center gap-3">
                    <Search size={18} /> Find me a property
                  </button> */}

                  <div className="h-px bg-slate-100 my-3"></div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 mb-1">Tools</p>

                  <button onClick={() => { setIsMenuOpen(false); setShowUpgradeModal(true); }} className="w-full text-left py-3 px-2 text-sm text-slate-400 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <div className="flex items-center gap-3 flex-1">
                      <Calculator size={18} /> ROI Calculator
                    </div>
                    <Lock size={12} className="text-amber-500" />
                  </button>

                  <button onClick={() => { onNavigateToRentalYield(); setIsMenuOpen(false); }} className="w-full text-left py-3 px-2 text-sm text-emerald-600 font-medium flex items-center gap-3 hover:bg-emerald-50 rounded-lg transition-colors min-h-[48px]">
                    <div className="flex items-center gap-3 flex-1">
                      <Percent size={18} /> Rental Yield
                    </div>
                  </button>

                  <button onClick={() => { setIsMenuOpen(false); setShowUpgradeModal(true); }} className="w-full text-left py-3 px-2 text-sm text-slate-400 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <div className="flex items-center gap-3 flex-1">
                      <Landmark size={18} /> Mortgage Estimator
                    </div>
                    <Lock size={12} className="text-amber-500" />
                  </button>

                  <button onClick={() => { setIsMenuOpen(false); setShowUpgradeModal(true); }} className="w-full text-left py-3 px-2 text-sm text-slate-400 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <div className="flex items-center gap-3 flex-1">
                      <FileText size={18} /> Market Reports
                    </div>
                    <Lock size={12} className="text-amber-500" />
                  </button>

                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      if (isGuest) {
                        authGate.openGate('developer-hub', {
                          path: location.pathname,
                          action: 'access-developer-hub',
                          targetPath: '/developer-hub'
                        });
                      } else if (!hasInvestorAccount) {
                        window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                          detail: { path: '/developer-hub' }
                        }));
                      } else {
                        onNavigateToDeveloperHub();
                      }
                    }}
                    className={`w-full text-left py-3 px-2 text-sm flex items-center gap-3 rounded-lg transition-colors min-h-[48px] ${
                      !isGuest && !hasInvestorAccount
                        ? 'text-slate-400 hover:bg-slate-50'
                        : 'text-slate-700 font-medium hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Briefcase size={18} /> Developer Intel
                    </div>
                    {!isGuest && !hasInvestorAccount && (
                      <Lock size={12} className="text-amber-500" />
                    )}
                  </button>

                  <div className="h-px bg-slate-100 my-3"></div>

                  <button onClick={() => { onNavigateToProfile(); setIsMenuOpen(false); }} className="w-full text-left py-3 px-2 font-medium text-slate-700 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <User size={18} /> My Account
                  </button>

                  <button onClick={() => { onNavigateToSettings(); setIsMenuOpen(false); }} className="w-full text-left py-3 px-2 font-medium text-slate-700 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors min-h-[48px]">
                    <Settings size={18} /> Settings
                  </button>

                  <div className="h-px bg-slate-100 my-3"></div>

                  <button onClick={() => { onLogout(); setIsMenuOpen(false); }} className="w-full text-left py-3 px-2 font-medium text-red-600 flex items-center gap-3 hover:bg-red-50 rounded-lg transition-colors min-h-[48px]">
                    <LogOut size={18} /> Log Out
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Search Input (Hidden on mobile) */}
          <div className="hidden lg:flex items-center relative group">
            <SearchAutocomplete
              value={searchQuery}
              onChange={(query) => {
                setSearchQuery(query);
                if (onSearch) onSearch(query);
              }}
              onSelect={(property) => {
                if (onPropertyClick) {
                  onPropertyClick(property.id, property);
                } else {
                  navigate(getProjectUrl(property));
                }
                setSearchQuery('');
              }}
              placeholder="Search projects, areas, developers..."
              className="w-64"
              showSuggestions={true}
              maxSuggestions={6}
            />
          </div>

          <button
            onClick={onNavigateToShortlist}
            className="relative p-2.5 min-w-[44px] min-h-[44px] md:size-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <Bookmark size={18} />
            {shortlistCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                {shortlistCount > 99 ? '99+' : shortlistCount}
              </span>
            )}
          </button>

          <button 
            onClick={async () => await openIntercom()}
            className="p-2.5 min-w-[44px] min-h-[44px] md:size-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Messages"
          >
            <MessageCircle size={18} />
          </button>

          {/* Profile Dropdown or Login Button */}
          {isGuest ? (
            <button
              onClick={onLogout} // Repurposed for "Login" trigger in Guest mode
              className="ml-2 px-5 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-slate-800 transition-all active:scale-95 flex items-center gap-2"
            >
              <LogIn size={16} /> <span className="hidden sm:inline">Log In</span>
            </button>
          ) : (
            <div className="relative group h-16 flex items-center ml-1">
              <button
                className="size-10 md:size-8 rounded-full bg-slate-200 overflow-hidden ring-2 ring-white shadow-sm transition-transform hover:scale-105 active:scale-95 p-0.5"
              >
                <img
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuCks2E_sSbmLM75cE5R99VQSuRUX2Yl__71gQ08SgcZ9z9WlhjB1IaEmowupGcrmmI2qKZ-seY5VHJ8Z07Rm4rG518CVLxI4I315cH_2y0P2Wky14Z45Wv721sOgZT3C-CMs9p4CNwx_-OvijV2PNl8lC8YnDcSuhyMmkmZQR9W-_y6kFoxMPSr01zHGgSFferxbDokqVRRetp5nI-YEEQ2aCErX6Z62tw821nyJu31yisXrwIUM93KND0PBdk_dUDY9zAMOLa2Qg"
                  alt="Profile"
                  className="size-full object-cover"
                />
              </button>

              {/* Profile Dropdown */}
              <div className="absolute top-full right-0 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-50">
                <button
                  onClick={onNavigateToProfile}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                >
                  <User size={16} /> My Account
                </button>
                <button
                  onClick={onNavigateToSettings}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                >
                  <Settings size={16} /> Settings
                </button>
                <div className="my-1 border-t border-slate-100"></div>
                <button
                  onClick={onLogout}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                >
                  <LogOut size={16} /> Log Out
                </button>
              </div>
            </div>
          )}

        </div>
      </nav>

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        source="investor_tools"
      />
    </div>
  );
};

export default Navbar;
